import { useEffect, useState } from 'react'
import { Accordion, AccordionItem, addToast, Button } from '@heroui/react'
import { useTranslation } from 'react-i18next'
import { usePostHog } from '@posthog/react'
import { createSearchParams, useNavigate } from 'react-router'
import { RefundPaymentMethod, ReturnMethod } from '@itsrever/returns-api-types'
import { useTheme } from '@/ui/theme'
import { usePages, useTranslate } from '@/hooks'
import { useProcess } from '@/hooks/use-process'
import { useAppSelector } from '@/redux/hooks'
import { useSummary } from '@/hooks/use-summary'
import type { IbanFormData } from '@/app/confirmation/bank-transfer/iban-form'
import type { InternationalFormData } from '@/app/confirmation/bank-transfer/international-form'
import { StripeCheckout } from '@/app/confirmation/stripe-checkout'
import { ShippingAddress } from '@/app/confirmation/shipping-address'
import { BankTransfer } from '@/app/confirmation/bank-transfer/bank-transfer'
import { RefundTiming } from '@/app/confirmation/refund-timing'
import { ReturnDetails } from '@/app/confirmation/return-details'
import { Summary } from '@/app/confirmation/summary/summary'
import { LoadingOverlay } from '@/ui/components/loading-overlay'
import { GiftCardOffer } from '@/app/confirmation/gift-card-offer'
import { ShippingTiming } from '@/app/confirmation/shipping-timing'
import { NavArrowLeftIcon } from '@/ui/icons/nav-arrow-left'
import { MisakoHeader } from '@/ui/custom/misako-header'

export const ConfirmationPage = () => {
  const { i18n, t } = useTranslation()
  const { translate } = useTranslate()

  const theme = useTheme()

  const posthog = usePostHog()

  const navigate = useNavigate()
  const { pages } = usePages()

  const [selectedAccordionKeys, setSelectedAccordionKeys] = useState(
    new Set(['summary'])
  )
  const [giftCardOfferSelected, setGiftCardOfferSelected] = useState(false)
  const [isInstantExchangeTimingSelected, setIsInstantExchangeTimingSelected] =
    useState(false)

  const settings = useAppSelector(
    (store) => store.settingsApi.settings.response
  )

  const isInStore = settings.in_store_settings?.is_enabled

  const refundMethods = useAppSelector(
    (store) => store.refundMethodsApi.refundMethods.response
  )

  const selectedRefundMethod = useAppSelector(
    (store) => store.return.refundPaymentMethod
  )

  const order = useAppSelector((store) => store.returnsApi.order.response)

  const giftCardMethod = refundMethods.result?.refund_methods?.find(
    ({ method }) =>
      method === RefundPaymentMethod.RefundPaymentMethod_GIFT_CARD ||
      method === RefundPaymentMethod.RefundPaymentMethod_PROMO_CODE
  )

  const { data: summary, isFetching: isFetchingSummary } = useSummary({
    ...(isInstantExchangeTimingSelected
      ? { is_exchange_instant: isInstantExchangeTimingSelected }
      : {}),
    ...(giftCardOfferSelected ? { refund_method: giftCardMethod?.method } : {})
  })

  const exchangeItems = summary?.calculated_summary?.exchange_items ?? []

  const refund = summary?.signed_summary?.return_summary?.refund

  const totalRefundAmount =
    summary?.signed_summary?.return_summary?.total_refund_amount ?? 0

  const totalPaymentAmount =
    summary?.signed_summary?.return_summary?.total_payment_amount ?? 0

  const isBankTransfer =
    refund?.refund_method ===
      RefundPaymentMethod.RefundPaymentMethod_BANK_TRANSFER &&
    totalRefundAmount > 0

  const shouldShowGiftCardOffer =
    giftCardMethod &&
    selectedRefundMethod.method !== giftCardMethod.method &&
    exchangeItems.length === 0

  const {
    mutate,
    isPending: isPendingProcess,
    data: processData,
    reset: resetProcess
  } = useProcess()

  const handleComplete = (
    data?: Partial<IbanFormData & InternationalFormData>
  ) => {
    if (!settings.ecommerce_id || !order.checkout_id) return

    if (totalPaymentAmount > 0) {
      posthog.capture('Checkout', {
        total_payment_amount: totalPaymentAmount,
        currency: order.order?.money_format?.currency,
        checkout_id: order.checkout_id
      })
    }

    mutate(
      {
        ecommerceID: settings.ecommerce_id,
        processData: {
          iban: data?.iban,
          country_code: data?.country_code,
          swift: data?.swift,
          bban: data?.bban,
          user_preferred_lang: i18n.language,
          signed_return_summary_raw: summary?.signed_summary_raw,
          signed_refund_methods_raw: refundMethods.result_raw,
          fraud_result: refundMethods.result?.fraud_result,
          checkout_id: order.checkout_id
        }
      },
      {
        onSuccess: (data) => {
          if (data.payment_session_id) {
            setSelectedAccordionKeys(new Set([]))
            return
          }

          posthog.capture('Completed', {
            checkout_id: order.checkout_id,
            process_id: data.pid
          })

          navigate({
            pathname: pages.SuccessTracking,
            search: createSearchParams({
              processId: data.pid ?? '',
              email: order.order?.customer?.email ?? '',
              orderNumber:
                order.order?.identification?.customer_printed_order_id ?? ''
            }).toString()
          })
        },
        onError: () => {
          addToast({
            description: t('toast_errors.error_406'),
            color: 'danger'
          })
        }
      }
    )
  }

  useEffect(() => {
    if (summary) {
      resetProcess()

      posthog.capture('Confirmation Page Loaded', {
        checkout_id: order.checkout_id,
        currency: order.order?.money_format?.currency,
        summary: {
          returned_items: summary?.calculated_summary?.returned_items,
          exchange_items: summary?.calculated_summary?.exchange_items,
          refund_method:
            summary?.signed_summary?.return_summary?.refund?.refund_method,
          refund_timing:
            summary?.signed_summary?.return_summary?.refund?.timing,
          return_method:
            summary?.signed_summary?.return_summary?.return?.return_method,
          line_items: summary?.signed_summary?.return_summary?.line_items,
          total_refund_amount:
            summary?.signed_summary?.return_summary?.total_refund_amount,
          total_payment_amount:
            summary?.signed_summary?.return_summary?.total_payment_amount
        }
      })
    }
  }, [posthog, summary, resetProcess, order])

  const color = theme.colors.primary.main
  const textColor = theme.colors.primary.contrastText

  if (!order?.checkout_id || !summary?.calculated_summary) {
    return <LoadingOverlay loading />
  }

  const isMisako = settings.ecommerce_group === 'misako'
  const isHoff = settings.ecommerce_group === 'hoff'

  if (isMisako || isHoff) {
    return (
      <>
        {isFetchingSummary || isPendingProcess ? (
          <LoadingOverlay loading />
        ) : null}
        {isMisako ? <MisakoHeader /> : null}
        <div className="flex-1 bg-white">
          <div className="mx-auto max-w-6xl py-5 lg:p-6">
            <div className="mx-auto max-w-xl px-4 pb-3 lg:mx-0 lg:max-w-full lg:px-6">
              <a
                onClick={() => navigate(-1)}
                className="flex cursor-pointer items-center gap-3 text-sm font-light text-neutral-600 underline-offset-4 hover:underline"
              >
                <NavArrowLeftIcon className="size-4" />
                {translate('top_bar_back')}
              </a>
            </div>
            <main className="grid gap-6 lg:grid-cols-10">
              <div className="space-y-3 lg:col-span-6">
                <section className="h-fit">
                  {summary && (
                    <Accordion
                      className="block border-b border-solid border-neutral-200 px-0 py-4 lg:hidden"
                      selectedKeys={selectedAccordionKeys}
                      onSelectionChange={(keys) =>
                        setSelectedAccordionKeys(keys as Set<string>)
                      }
                    >
                      <AccordionItem
                        key="summary"
                        textValue={translate('confirmation_page_summary_title')}
                        title={
                          <h1
                            className="text-lg font-medium"
                            dangerouslySetInnerHTML={{
                              __html: translate(
                                'confirmation_page_summary_title'
                              )
                            }}
                          />
                        }
                        classNames={{
                          trigger: 'py-0 px-4 max-w-xl mx-auto',
                          indicator: 'data-[open=true]:rotate-90 -rotate-90',
                          content: 'px-4 space-y-4 max-w-xl mx-auto'
                        }}
                      >
                        <Summary summary={summary} />
                        {shouldShowGiftCardOffer && (
                          <GiftCardOffer
                            giftCardMethod={giftCardMethod}
                            selected={giftCardOfferSelected}
                            onSelectedChange={setGiftCardOfferSelected}
                          />
                        )}
                      </AccordionItem>
                    </Accordion>
                  )}

                  <div className="mx-auto max-w-xl space-y-6 px-4 py-6 lg:max-w-full lg:px-6 lg:py-5">
                    {summary && <ReturnDetails summary={summary} />}

                    {totalRefundAmount > 0 && refund?.timing && (
                      <RefundTiming timing={refund.timing} />
                    )}

                    {exchangeItems.length > 0 &&
                      summary?.signed_summary?.return_summary?.return
                        ?.return_method !==
                        ReturnMethod.ReturnMethod_DELIVERY_PICKUP &&
                      !isInStore && (
                        <>
                          <ShippingAddress />
                          <ShippingTiming
                            onSelect={setIsInstantExchangeTimingSelected}
                          />
                        </>
                      )}

                    {processData && processData.payment_session_id ? (
                      <StripeCheckout
                        process={processData}
                        paymentAmount={totalPaymentAmount}
                      />
                    ) : (
                      <>
                        {isBankTransfer && (
                          <BankTransfer onSubmit={handleComplete} />
                        )}
                        <Button
                          id="confirmButton"
                          isLoading={isPendingProcess}
                          fullWidth
                          radius="sm"
                          size="lg"
                          form={
                            isBankTransfer ? 'bank-transfer-form' : undefined
                          }
                          type={isBankTransfer ? 'submit' : 'button'}
                          onPress={
                            isBankTransfer
                              ? undefined
                              : () => handleComplete(undefined)
                          }
                          className="text-sm"
                          style={{
                            backgroundColor: color,
                            color: textColor,
                            borderRadius: theme.borderRadius
                          }}
                        >
                          {totalPaymentAmount > 0
                            ? translate('confirmation_page_continue_to_payment')
                            : translate('confirmation_page_button_complete')}
                        </Button>
                      </>
                    )}
                  </div>
                </section>
              </div>
              <section className="hidden h-fit space-y-4 px-6 py-5 lg:sticky lg:col-span-4 lg:block">
                {summary && <Summary summary={summary} />}
                {shouldShowGiftCardOffer && (
                  <GiftCardOffer
                    giftCardMethod={giftCardMethod}
                    selected={giftCardOfferSelected}
                    onSelectedChange={setGiftCardOfferSelected}
                  />
                )}
              </section>
            </main>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      {isFetchingSummary || isPendingProcess ? (
        <LoadingOverlay loading />
      ) : null}
      <div className="flex-1 bg-white lg:bg-[#F2F2F2]">
        <div className="mx-auto max-w-6xl lg:px-6 lg:py-8">
          <main className="grid gap-6 lg:grid-cols-10">
            <div className="space-y-3 lg:col-span-6">
              <section className="h-fit rounded-lg bg-white lg:shadow-sm">
                {summary && (
                  <Accordion
                    className="block border-b border-solid border-neutral-200 px-0 py-4 lg:hidden"
                    selectedKeys={selectedAccordionKeys}
                    onSelectionChange={(keys) =>
                      setSelectedAccordionKeys(keys as Set<string>)
                    }
                  >
                    <AccordionItem
                      key="summary"
                      textValue={translate('confirmation_page_summary_title')}
                      title={
                        <h1
                          className="text-lg font-medium"
                          dangerouslySetInnerHTML={{
                            __html: translate('confirmation_page_summary_title')
                          }}
                        />
                      }
                      classNames={{
                        trigger: 'py-0 px-4 max-w-xl mx-auto',
                        indicator: 'data-[open=true]:rotate-90 -rotate-90',
                        content: 'px-4 space-y-4 max-w-xl mx-auto'
                      }}
                    >
                      <Summary summary={summary} />
                      {shouldShowGiftCardOffer && (
                        <GiftCardOffer
                          giftCardMethod={giftCardMethod}
                          selected={giftCardOfferSelected}
                          onSelectedChange={setGiftCardOfferSelected}
                        />
                      )}
                    </AccordionItem>
                  </Accordion>
                )}

                <div className="mx-auto max-w-xl space-y-6 px-4 py-6 lg:max-w-full lg:px-6 lg:py-5">
                  {summary && <ReturnDetails summary={summary} />}

                  {totalRefundAmount > 0 && refund?.timing && (
                    <RefundTiming timing={refund.timing} />
                  )}

                  {exchangeItems.length > 0 &&
                    summary?.signed_summary?.return_summary?.return
                      ?.return_method !==
                      ReturnMethod.ReturnMethod_DELIVERY_PICKUP &&
                    !isInStore && (
                      <>
                        <ShippingAddress />
                        <ShippingTiming
                          onSelect={setIsInstantExchangeTimingSelected}
                        />
                      </>
                    )}

                  {processData && processData.payment_session_id ? (
                    <StripeCheckout
                      process={processData}
                      paymentAmount={totalPaymentAmount}
                    />
                  ) : (
                    <>
                      {isBankTransfer && (
                        <BankTransfer onSubmit={handleComplete} />
                      )}
                      <Button
                        id="confirmButton"
                        isLoading={isPendingProcess}
                        fullWidth
                        radius="sm"
                        size="lg"
                        form={isBankTransfer ? 'bank-transfer-form' : undefined}
                        type={isBankTransfer ? 'submit' : 'button'}
                        onPress={
                          isBankTransfer
                            ? undefined
                            : () => handleComplete(undefined)
                        }
                        style={{
                          backgroundColor: color,
                          color: textColor,
                          borderRadius: theme.borderRadius
                        }}
                      >
                        {totalPaymentAmount > 0
                          ? translate('confirmation_page_continue_to_payment')
                          : translate('confirmation_page_button_complete')}
                      </Button>
                    </>
                  )}
                </div>
              </section>
            </div>
            <section className="hidden h-fit space-y-4 rounded-lg bg-white px-6 py-5 shadow-sm lg:sticky lg:top-28 lg:col-span-4 lg:block">
              {summary && <Summary summary={summary} />}
              {shouldShowGiftCardOffer && (
                <GiftCardOffer
                  giftCardMethod={giftCardMethod}
                  selected={giftCardOfferSelected}
                  onSelectedChange={setGiftCardOfferSelected}
                />
              )}
            </section>
          </main>
        </div>
      </div>
    </>
  )
}
